import xhrService from '../xhrService';

export type AssetFieldType =
  | 'text'
  | 'number'
  | 'select'
  | 'date'
  | 'yes_no'
  | 'yes_no_na';

export interface AssetTypeField {
  id?: number;
  asset_type_id?: number;
  label: string;
  field_type: AssetFieldType;
  metadata?: { unit?: string; options?: string[] } | null;
  is_required: boolean;
  is_inspectable: boolean;
  is_active: boolean;
  display_order: number;
}

export interface AssetSymbol {
  color?: string;
  code?: string;
}

export interface AssetType {
  id: number;
  division_id: number | null;
  name: string;
  description?: string;
  code: string;
  is_system: boolean;
  display_order: number;
  fields?: AssetTypeField[];
  // Map/print symbol, stored in the featureable table.
  featureable_data?: {
    type?: string;
    config?: AssetSymbol;
    dataurl?: string;
  } | null;
}

export interface AssetStatus {
  id: number;
  division_id: number | null;
  name: string;
  color?: string;
  is_default: boolean;
  sort_order: number;
}

export const assetTypesApi = {
  index: (divisionId: number): Promise<AssetType[]> =>
    xhrService
      .get('/api/asset-types', { params: { division_id: divisionId } })
      .then((r) => r.data),

  create: (divisionId: number, data: Partial<AssetType>): Promise<AssetType> =>
    xhrService
      .post('/api/asset-types', data, { params: { division_id: divisionId } })
      .then((r) => r.data),

  update: (id: number, data: Partial<AssetType>): Promise<AssetType> =>
    xhrService.put(`/api/asset-types/${id}`, data).then((r) => r.data),

  destroy: (id: number): Promise<void> =>
    xhrService.delete(`/api/asset-types/${id}`).then(() => undefined),

  reorder: (items: { id: number; display_order: number }[]): Promise<void> =>
    xhrService
      .put('/api/asset-types/reorder', { items })
      .then(() => undefined),

  // Copy asset types (with fields + map symbol) into one or more divisions.
  copy: (
    divisionIds: Array<{ id: number }>,
    assetTypeIds: number[],
  ): Promise<void> =>
    xhrService
      .post('/api/asset-types/copy', { divisionIds, assetTypeIds })
      .then(() => undefined),

  fields: {
    create: (
      assetTypeId: number,
      data: Partial<AssetTypeField>,
    ): Promise<AssetTypeField> =>
      xhrService
        .post(`/api/asset-types/${assetTypeId}/fields`, data)
        .then((r) => r.data),

    update: (
      assetTypeId: number,
      fieldId: number,
      data: Partial<AssetTypeField>,
    ): Promise<AssetTypeField> =>
      xhrService
        .put(`/api/asset-types/${assetTypeId}/fields/${fieldId}`, data)
        .then((r) => r.data),

    destroy: (assetTypeId: number, fieldId: number): Promise<void> =>
      xhrService
        .delete(`/api/asset-types/${assetTypeId}/fields/${fieldId}`)
        .then(() => undefined),

    reorder: (
      assetTypeId: number,
      items: { id: number; display_order: number }[],
    ): Promise<void> =>
      xhrService
        .put(`/api/asset-types/${assetTypeId}/fields/reorder`, { items })
        .then(() => undefined),
  },
};

export const assetStatusesApi = {
  index: (divisionId: number): Promise<AssetStatus[]> =>
    xhrService
      .get('/api/asset-statuses', { params: { division_id: divisionId } })
      .then((r) => r.data),

  create: (
    divisionId: number,
    data: Partial<AssetStatus>,
  ): Promise<AssetStatus> =>
    xhrService
      .post('/api/asset-statuses', data, {
        params: { division_id: divisionId },
      })
      .then((r) => r.data),

  update: (id: number, data: Partial<AssetStatus>): Promise<AssetStatus> =>
    xhrService.put(`/api/asset-statuses/${id}`, data).then((r) => r.data),

  destroy: (id: number): Promise<void> =>
    xhrService.delete(`/api/asset-statuses/${id}`).then(() => undefined),

  reorder: (items: { id: number; sort_order: number }[]): Promise<void> =>
    xhrService
      .put('/api/asset-statuses/reorder', { items })
      .then(() => undefined),

  // Copy statuses into one or more divisions.
  copy: (
    divisionIds: Array<{ id: number }>,
    statusIds: number[],
  ): Promise<void> =>
    xhrService
      .post('/api/asset-statuses/copy', { divisionIds, statusIds })
      .then(() => undefined),
};
